import React from "react"
import clsx from "clsx"
import { Text } from "@gluestack-ui/themed"

type TypographyProps = {
  variant?:
    | "h1"
    | "h2"
    | "h3"
    | "h4"
    | "h5"
    | "h6"
    | "subtitle1"
    | "subtitle2"
    | "body1"
    | "body2"
    | "caption"
    | "overline"
  component?: React.ElementType
  color?: string
  gutterBottom?: boolean
  fontWeight?: number | string
  textAlign?: React.CSSProperties["textAlign"]
  className?: string
  sx?: Record<string, any>
  children?: React.ReactNode
  style?: React.CSSProperties
} & Record<string, any>

const resolveResponsive = (value: any) => {
  if (value && typeof value === "object") {
    const firstKey = Object.keys(value)[0]
    return value[firstKey]
  }
  return value
}

const variantMap: Record<string, any> = {
  h1: { variant: "h1", weight: "bold" },
  h2: { variant: "h2", weight: "bold" },
  h3: { variant: "h3", weight: "bold" },
  h4: { variant: "h4", weight: "bold" },
  h5: { variant: "h5", weight: "semibold" },
  h6: { variant: "h6", weight: "semibold" },
  subtitle1: { variant: "subtitle1", weight: "medium" },
  subtitle2: { variant: "subtitle2", weight: "medium" },
  body1: { variant: "body1", weight: "normal" },
  body2: { variant: "body2", weight: "normal" },
  caption: { variant: "caption", weight: "normal" },
  overline: { variant: "overline", weight: "medium" },
}

const defaultComponentForVariant: Record<string, React.ElementType> = {
  h1: "h1",
  h2: "h2",
  h3: "h3",
  h4: "h4",
  h5: "h5",
  h6: "h6",
  subtitle1: "h6",
  subtitle2: "h6",
  body1: "p",
  body2: "p",
  caption: "span",
  overline: "span",
}

const Typography = React.forwardRef<HTMLElement, TypographyProps>(
  (
    {
      variant = "body1",
      component,
      color,
      gutterBottom,
      fontWeight,
      textAlign,
      className,
      sx,
      style,
      children,
      ...rest
    },
    ref,
  ) => {
    const mapped = variantMap[variant] || { variant: "body1", weight: "normal" }
    const resolvedStyle: React.CSSProperties = {
      color,
      textAlign,
      fontWeight,
      marginBottom: gutterBottom ? "0.35em" : undefined,
      ...((sx && typeof sx === "object"
        ? Object.fromEntries(
            Object.entries(sx).map(([key, val]) => [key, resolveResponsive(val)]),
          )
        : {}) as React.CSSProperties),
      ...(style || {}),
    }

    const as = component || defaultComponentForVariant[variant] || "p"

    return (
      <Text
        as={as}
        variant={mapped.variant}
        weight={(fontWeight as any) || mapped.weight}
        className={clsx(className)}
        style={resolvedStyle}
        ref={ref as any}
        {...rest}
      >
        {children}
      </Text>
    )
  },
)

Typography.displayName = "Typography"

export default Typography
